import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';
import SEO from '../../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HiveSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const token = localStorage.getItem('hive_token');
    
    if (!sessionId || !token) {
      navigate('/hive/login');
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(
          `${API}/hive/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          
          // Update local user data with tier info
          const user = JSON.parse(localStorage.getItem('hive_user') || '{}');
          user.subscription_status = 'trial';
          user.subscription_tier = res.data.tier || 'hive_standalone';
          user.subscription_plan = res.data.plan;
          localStorage.setItem('hive_user', JSON.stringify(user));
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => navigate('/hive/dashboard'), 3000);
        } else if (res.data.status === 'expired') {
          setStatus('error');
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [searchParams, navigate, attempts]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="hive-success-page">
      <SEO 
        title="Payment Processing"
        description="Processing your Hive subscription payment."
      />
      
      <div className="text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-white/60">Please wait while we confirm your subscription.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-20 h-20 text-[#D4AF37] mx-auto mb-6" />
            <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-2">
              Welcome to <span className="text-[#D4AF37]">The Hive!</span>
            </h1>
            <p className="text-white/60 mb-4">Your subscription is now active.</p>
            <p className="text-white/40 text-sm">Redirecting to dashboard...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-2">
              Payment Issue
            </h1>
            <p className="text-white/60 mb-6">
              There was a problem processing your payment. Please try again.
            </p>
            <button
              onClick={() => navigate('/hive/subscribe')}
              className="btn-primary"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default HiveSuccessPage;
